import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router';
import { AppRoute, firstQueryValue } from '@/libs/routing';

import Alerts from '@/pages/AlertsPage/index.vue';
import HomePanel from '@/pages/MapPage/HomePanel.vue';
import Itinerary from '@/pages/ItineraryPage/index.vue';
import MapIndex from '@/pages/MapPage/MapIndex.vue';
import OnDemandRouteDetail from '@/pages/OnDemandRoutePage/index.vue';
import Settings from '@/pages/SettingsPage/index.vue';
import SettingsContact from '@/pages/SettingsPage/ContactPage.vue';
import SettingsLanguages from '@/pages/SettingsPage/LanguagesPage.vue';
import RouteAlerts from '@/pages/AlertsPage/RouteAlertsPage.vue';
import RouteDetail from '@/pages/RouteDetailPage/index.vue';
import RouteList from '@/pages/RoutesPage/RouteListPage.vue';
import Schedules from '@/pages/SchedulesPage/index.vue';
import StopDetail from '@/pages/StopDetailPage/index.vue';
import StopList from '@/pages/StopsPage/StopListPage.vue';
import SelectGroup from '@/pages/SelectGroupPage/index.vue';
import Timetable from '@/pages/TimetablePage/index.vue';
import TrainDepartures from '@/pages/TrainDeparturesPage/index.vue';
import TripDetail from '@/pages/TripDetailPage/index.vue';

import { useMatomo } from './use/matomo';
import Common from './libs/common';
import type { Group } from './@types/group';
import { Capacitor } from '@capacitor/core';

/**
 * Add a common prefix to some routes.
 */
const withPrefix = (prefix: string, routes: Array<RouteRecordRaw>) =>
  routes.map(route => {
    if (!route.path.startsWith('/')) {
      route.path = prefix + '/' + route.path;
    }
    return route;
  });

const getInitialRoute = (): string => {
  const groupId: Group | null = Common.safeLSParse('group');
  return groupId?.group_id ? `/${groupId?.group_id}/map` : '/select-group';
};

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '',
      redirect: getInitialRoute(),
    },

    {
      path: `/${AppRoute.SETTINGS}`,
      name: AppRoute.SETTINGS,
      component: Settings,
    },

    {
      path: `/${AppRoute.SETTINGS}/languages`,
      name: AppRoute.SETTINGS_LANGUAGES,
      component: SettingsLanguages,
    },

    {
      path: `/${AppRoute.SETTINGS}/contact`,
      name: AppRoute.SETTINGS_CONTACT,
      component: SettingsContact,
    },

    // The settings page used to live here.
    {
      path: '/profile',
      redirect: `/${AppRoute.SETTINGS}`,
    },

    {
      path: `/${AppRoute.SELECT_GROUP}`,
      name: AppRoute.SELECT_GROUP,
      component: SelectGroup,
    },

    ...withPrefix('/:groupId', [
      {
        path: `${AppRoute.PASSENGER_MESSAGE}/:alertId?`,
        name: AppRoute.PASSENGER_MESSAGE,
        component: Alerts,
        props: true,
      },

      {
        path: `${AppRoute.ROUTE_ALERTS}/:routeId/:alertId?`,
        name: AppRoute.ROUTE_ALERTS,
        component: RouteAlerts,
        props: true,
      },

      {
        path: AppRoute.MAP,
        component: MapIndex,
        children: [
          {
            path: '',
            name: AppRoute.MAP,
            component: HomePanel,
          },

          // The search keeps the home mounted underneath: MapIndex lays the
          // results panel over it and the search bar switches state in place.
          {
            path: AppRoute.SEARCH,
            name: AppRoute.SEARCH,
            component: HomePanel,
          },

          {
            path: `${AppRoute.ROUTE_DETAILED}/:routeId`,
            name: AppRoute.ROUTE_DETAILED,
            component: RouteDetail,
            props: true,
          },

          // Its own param name: MapComponent reads `routeId` as a GTFS route.
          {
            path: `${AppRoute.ON_DEMAND_ROUTE_DETAILED}/:onDemandRouteId`,
            name: AppRoute.ON_DEMAND_ROUTE_DETAILED,
            component: OnDemandRouteDetail,
            props: true,
          },

          // `?route=` narrows the stop to one line: reached from that line's detail,
          // the passenger is following it, not looking at everything calling here.
          {
            path: `${AppRoute.STOP_DETAILED}/:stopId`,
            name: AppRoute.STOP_DETAILED,
            component: StopDetail,
            props: route => ({
              stopId: route.params.stopId,
              routeId: firstQueryValue(route.query.route),
            }),
          },

          // `?stop=` is the stop the trip was opened from: the passenger waits there,
          // so its passage takes the enriched block instead of the vehicle's next stop.
          {
            path: `${AppRoute.TRIP_DETAILED}/:tripId`,
            name: AppRoute.TRIP_DETAILED,
            component: TripDetail,
            props: route => ({
              tripId: route.params.tripId,
              originStopId: firstQueryValue(route.query.stop),
            }),
          },

          // `?from=<stopId|lat,lon>&to=<stopId|lat,lon>`, `fromLabel` / `toLabel` naming an address:
          // without `from`, the journey leaves from the passenger's position.
          {
            path: AppRoute.ITINERARY,
            name: AppRoute.ITINERARY,
            component: Itinerary,
          },
        ],
      },

      {
        path: AppRoute.SCHEDULES,
        name: AppRoute.SCHEDULES,
        component: Schedules,
      },

      {
        path: AppRoute.ROUTE_LIST,
        name: AppRoute.ROUTE_LIST,
        component: RouteList,
      },

      {
        path: AppRoute.STOP_LIST,
        name: AppRoute.STOP_LIST,
        component: StopList,
      },

      {
        path: `${AppRoute.TIMETABLE}/:stopId`,
        name: AppRoute.TIMETABLE,
        component: Timetable,
        props: true,
      },

      {
        path: `${AppRoute.TRAIN_DEPARTURES}/:stopId`,
        name: AppRoute.TRAIN_DEPARTURES,
        component: TrainDepartures,
        props: true,
      },
    ]),
  ],
});

router.afterEach(route => {
  const { matomo } = useMatomo();
  if (matomo) {
    // send pageview to analytics with url formed as: /info/route-name
    const url = `/info/${route.name?.toString()}`;
    matomo.trackPageView(url);
  }

  if (Capacitor.isNativePlatform()) {
    // In edge-to-edge mode, safe-area insets are handled via CSS.
    // On map pages, content extends behind system bars (no padding).
    // On other pages, .native-app CSS applies safe-area padding.
    const isMapPage = route.name
      ? [
          AppRoute.MAP,
          AppRoute.SEARCH,
          AppRoute.ROUTE_DETAILED,
          AppRoute.ON_DEMAND_ROUTE_DETAILED,
          AppRoute.STOP_DETAILED,
          AppRoute.TRIP_DETAILED,
          AppRoute.ITINERARY,
          AppRoute.SELECT_GROUP,
        ].includes(route.name?.toString())
      : false;
    document.documentElement.classList.toggle('native-map-page', isMapPage);
  }
});

export default router;
